import {useAccessToken} from "../../services/auth"
import React, {Dispatch, SetStateAction, useEffect, useRef, useState} from "react"
import {ApplicationFormAdditionalDisclosure, OrgSettings, update} from "../../resources/orgSettings"
import useAsyncResultIdle from "../../hooks/useAsyncResultIdle"
import {useToasts} from "react-toast-notifications"
import Editor from "@monaco-editor/react"
import {useModal} from "react-modal-hook"
import {ApplicationFormPreviewModal} from "./ApplicationFormPreviewModal"
import {ConfirmationModal} from "../../components/ConfirmationModal/ConfirmationModal"
import KeyValueEditor from "../../components/KeyValueEditor/KeyValueEditor"
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome"
import {faInfoCircle} from "@fortawesome/free-solid-svg-icons"
import ReactTooltip from "react-tooltip"
import Admonition from "../../components/Admonitions/Admonition"
import FeatureHighlightBanner from "../../components/FeatureHighlightBanner/FeatureHighlightBanner"
import FeatureHighlightImage from "../../images/feature-highlight.svg"
import Icon from "../../components/Icon/Icon"
import {ApplicationFormVersion} from "../../resources/applicationForm"
import {useLocalStorage} from "react-use"
import CustomDropdown from "../../components/CustomDropdown/CustomDropdown"
import Tag from "../../components/Tag/Tag"
import {useAsyncResult} from "../../hooks/useAsyncResult"
import {getEmailOutreachSettings} from "../../resources/outreachSettings"
import {isUndefined} from "lodash"

// Relevant for when the user has to save changes before a preview:
// Using a module variable as a flag instead of a component state because the whole component (ApplicationFormSettings)
// will be "refreshed" after a successful form submit (we want to remember to open the preview modal without using React
// state, because React state will be reset to their default values)
let shouldShowPreviewModal = false

const versionsDropdownOptions = [
	{
		value: ApplicationFormVersion.V1,
		key: ApplicationFormVersion.V1,
		content: (
			<>
				<span style={{width: 30, display: "flex", alignItems: "center", justifyContent: "center"}}>
					<Icon icon="alert-triangle--interface-essential" size={16} color={"#FF506F"} />
				</span>{" "}
				Application Form
			</>
		),
	},
	{
		value: ApplicationFormVersion.V2,
		key: ApplicationFormVersion.V2,
		content: (
			<>
				<Tag size={"small"} content={"New"} variant={"filled"} theme={"info"} />
				White-Label Application Form
			</>
		),
	},
]

export default function ApplicationFormSettings({
	orgSettings,
	refresh,
}: {
	orgSettings: OrgSettings
	refresh: () => void
}) {
	const accessToken = useAccessToken()
	const [selectedVersion, setSelectedVersion] = useLocalStorage<ApplicationFormVersion>(
		"orgSettingsApplicationFormVersion",
		ApplicationFormVersion.V2
	)
	const [showApplicationFormV2FeatureHighlightBanner, setShowApplicationFormV2FeatureHighlightBanner] = useLocalStorage(
		"showApplicationFormV2FeatureHighlightBanner",
		true
	)
	const [state, patch] = useAsyncResultIdle(update)
	const [showPreviewModal, hidePreviewModal] = useModal(
		() => (
			<ApplicationFormPreviewModal
				close={hidePreviewModal}
				accessToken={accessToken}
				applicationFormVersion={selectedVersion || ApplicationFormVersion.V2}
			/>
		),
		[selectedVersion]
	)
	const [showPreviewSaveConfirmationModal, hidePreviewSaveConfirmationModal] = useModal(
		() => (
			<PreviewSaveConfirmationModal
				close={hidePreviewSaveConfirmationModal}
				onSubmit={previewSaveConfirmationModalConfirmed}
			/>
		),
		[createPatchObject]
	)
	const formElement = useRef<HTMLFormElement>(null)

	const cssEditorPlaceholder = `/*
    The CSS on the application form can be fully customized for your needs.
    We recommend overriding our preset CSS variables, for example:
        :root {
            --header-background-color: #d5ffd5;
            --stage-indicator-background-color: #cef1ce;
            --success-color: green;
        }
        
    A full list of our preset CSS variables can be obtained by inspecting the application form <html> tag 
    using the browser's Developer Tools (see the ":root" selector there)
*/`

	const [applicationFormLogo, setApplicationFormLogo] = useState<string | undefined>(
		orgSettings?.attributes.applicationFormSettings.applicationFormLogo ?? undefined
	)
	const [applicationFormFavicon, setApplicationFormFavicon] = useState<string | undefined>(
		orgSettings?.attributes.applicationFormSettings.applicationFormFavicon ?? undefined
	)
	const [applicationFormBrandName, setApplicationFormBrandName] = useState<string | undefined>(
		orgSettings?.attributes.applicationFormSettings.applicationFormBrandName ?? undefined
	)
	const [applicationFormRedirectUrl, setApplicationFormRedirectUrl] = useState<string | undefined>(
		orgSettings?.attributes.applicationFormSettings.applicationFormRedirectUrl ?? undefined
	)
	const [applicationFormRedirectButtonText, setApplicationFormRedirectButtonText] = useState<string | undefined>(
		orgSettings?.attributes.applicationFormSettings.applicationFormRedirectButtonText ?? undefined
	)
	const [applicationFormDeniedRedirectUrl, setApplicationFormDeniedRedirectUrl] = useState<string | undefined>(
		orgSettings?.attributes.applicationFormSettings.applicationFormDeniedRedirectUrl ?? undefined
	)
	const [applicationFormPrivacyPolicyUrl, setApplicationFormPrivacyPolicyUrl] = useState<string | undefined>(
		orgSettings?.attributes.applicationFormSettings.applicationFormPrivacyPolicyUrl ?? undefined
	)
	const [applicationFormElectronicDisclosuresUrl, setApplicationFormElectronicDisclosuresUrl] = useState<
		string | undefined
	>(orgSettings?.attributes.applicationFormSettings.applicationFormElectronicDisclosuresUrl ?? undefined)
	const [applicationFormDepositTermsUrl, setApplicationFormDepositTermsUrl] = useState<string | undefined>(
		orgSettings?.attributes.applicationFormSettings.applicationFormDepositTermsUrl ?? undefined
	)
	const [applicationFormClientTermsUrl, setApplicationFormClientTermsUrl] = useState<string | undefined>(
		orgSettings?.attributes.applicationFormSettings.applicationFormClientTermsUrl ?? undefined
	)
	const [applicationFormCardholderTermsUrl, setApplicationFormCardholderTermsUrl] = useState<string | undefined>(
		orgSettings?.attributes.applicationFormSettings.applicationFormCardholderTermsUrl ?? undefined
	)
	const [applicationFormCashAdvancedTermsUrl, setApplicationFormCashAdvancedTermsUrl] = useState<string | undefined>(
		orgSettings?.attributes.applicationFormSettings.applicationFormCashAdvancedTermsUrl ?? undefined
	)
	const [applicationFormDebitCardDisclosureUrl, setApplicationFormDebitCardDisclosureUrl] = useState<
		string | undefined
	>(orgSettings?.attributes.applicationFormSettings.applicationFormDebitCardDisclosureUrl ?? undefined)
	const [applicationFormAdditionalDisclosures, setApplicationFormAdditionalDisclosures] = useState<
		Array<ApplicationFormAdditionalDisclosure> | undefined
	>(orgSettings?.attributes.applicationFormSettings.applicationFormAdditionalDisclosures ?? [])
	const [applicationFormContactUsEmail, setApplicationFormContactUsEmail] = useState<string | undefined>(
		orgSettings?.attributes.applicationFormSettings.applicationFormContactUsEmail ?? undefined
	)
	const [applicationFormCustomCss, setApplicationFormCustomCss] = useState<string | undefined>(
		orgSettings?.attributes.applicationFormSettings.applicationFormCustomCss ?? cssEditorPlaceholder
	)
	const [applicationFormValidatePhoneNumber, setApplicationFormValidatePhoneNumber] = useState(
		orgSettings?.attributes.applicationFormSettings.applicationFormValidatePhoneNumber ?? undefined
	)
	const [applicationFormUseSelfieVerification, setApplicationFormUseSelfieVerification] = useState<boolean | undefined>(
		orgSettings?.attributes.applicationFormSettings.applicationFormUseSelfieVerification
	)
	const [applicationFormShowElectronicDisclosureStage, setApplicationFormShowElectronicDisclosureStage] = useState<
		boolean | undefined
	>(orgSettings?.attributes.applicationFormSettings.applicationFormShowElectronicDisclosureStage ?? undefined)
	const [applicationFormShowTaxStage, setApplicationFormShowTaxStage] = useState<boolean | undefined>(
		orgSettings?.attributes.applicationFormSettings.applicationFormShowTaxStage ?? undefined
	)
	const [applicationFormEnableOutreach, setApplicationFormEnableOutreach] = useState<boolean | undefined>(
		orgSettings?.attributes.applicationFormSettings.applicationFormEnableOutreach
	)
	const [bankingPageURL, setBankingPageURL] = useState<string | undefined>(
		orgSettings?.attributes.applicationFormSettings.bankingPageURL ?? undefined
	)
	const outreachSettings = useAsyncResult(() => getEmailOutreachSettings(accessToken), [])
	const [applicationFormOutreachSettingsId, setApplicationFormOutreachSettingsId] = useState<string | undefined>(
		orgSettings?.attributes.applicationFormSettings.applicationFormOutreachSettingsId ?? undefined
	)
	const {addToast} = useToasts()

	useEffect(() => {
		// On first render only (componentDidMount), handle showing the preview modal
		// (only relevant if the user had to save changes before preview)
		if (shouldShowPreviewModal) {
			showPreviewModal()
			shouldShowPreviewModal = false
		}
	}, [])

	useEffect(() => {
		if (state.isOk()) {
			addToast("Org Updated Successfully", {appearance: "success"})
			refresh()
		}
	}, [state])

	function onFileUploaded(
		e: React.ChangeEvent<HTMLInputElement>,
		setStateFunction: Dispatch<SetStateAction<string | undefined>>
	) {
		const files = e.target.files

		if (files) {
			const file = files[0]

			if (file) {
				const reader = new FileReader()

				reader.addEventListener(
					"load",
					function () {
						setStateFunction(reader.result ? reader.result.toString() : undefined)
					},
					false
				)

				if (file) {
					reader.readAsDataURL(file)
				}
			}
		}
	}

	function onFaviconImageLoadedSquareValidation(e: React.ChangeEvent<HTMLImageElement>) {
		const inputElement = document.querySelector(".favicon-input") as HTMLInputElement

		if (e.target.width !== e.target.height) {
			inputElement.setCustomValidity("Please use a square PNG image for the favicon")
		} else {
			inputElement.setCustomValidity("")
		}
	}

	function createPatchObject() {
		return {
			applicationFormLogo: applicationFormLogo == "" ? undefined : applicationFormLogo,
			applicationFormFavicon: applicationFormFavicon == "" ? undefined : applicationFormFavicon,
			applicationFormBrandName: applicationFormBrandName == "" ? undefined : applicationFormBrandName,
			applicationFormRedirectUrl: applicationFormRedirectUrl == "" ? undefined : applicationFormRedirectUrl,
			applicationFormRedirectButtonText:
				applicationFormRedirectButtonText == "" ? undefined : applicationFormRedirectButtonText,
			applicationFormDeniedRedirectUrl:
				applicationFormDeniedRedirectUrl == "" ? undefined : applicationFormDeniedRedirectUrl,
			applicationFormPrivacyPolicyUrl:
				applicationFormPrivacyPolicyUrl == "" ? undefined : applicationFormPrivacyPolicyUrl,
			applicationFormElectronicDisclosuresUrl:
				applicationFormElectronicDisclosuresUrl == "" ? undefined : applicationFormElectronicDisclosuresUrl,
			applicationFormDepositTermsUrl: applicationFormDepositTermsUrl == "" ? undefined : applicationFormDepositTermsUrl,
			applicationFormClientTermsUrl: applicationFormClientTermsUrl == "" ? undefined : applicationFormClientTermsUrl,
			applicationFormCardholderTermsUrl:
				applicationFormCardholderTermsUrl == "" ? undefined : applicationFormCardholderTermsUrl,
			applicationFormCashAdvancedTermsUrl:
				applicationFormCashAdvancedTermsUrl == "" ? undefined : applicationFormCashAdvancedTermsUrl,
			applicationFormDebitCardDisclosureUrl:
				applicationFormDebitCardDisclosureUrl == "" ? undefined : applicationFormDebitCardDisclosureUrl,
			applicationFormContactUsEmail: applicationFormContactUsEmail == "" ? undefined : applicationFormContactUsEmail,
			applicationFormCustomCss: applicationFormCustomCss == "" ? undefined : applicationFormCustomCss,
			applicationFormAdditionalDisclosures:
				applicationFormAdditionalDisclosures?.length == 0 ? undefined : applicationFormAdditionalDisclosures,
			applicationFormValidatePhoneNumber,
			applicationFormUseSelfieVerification,
			applicationFormShowElectronicDisclosureStage,
			applicationFormShowTaxStage,
			applicationFormEnableOutreach,
			bankingPageURL,
			applicationFormOutreachSettingsId,
		}
	}

	function hasFormChanged() {
		// Comparing between initial settings and the would-be settings (before the save) to understand if the form has
		// been changed in any way
		const currentFormSettings = orgSettings.attributes.applicationFormSettings
		const patchObject: Record<any, any> = createPatchObject()

		// Remove any undefined values from the patch object (they're not interesting in the context of comparing values)
		Object.keys(patchObject).forEach((key) => (patchObject[key] === undefined ? delete patchObject[key] : {}))

		// Actual comparison using a key-sorted JSON string
		return (
			JSON.stringify(currentFormSettings, Object.keys(currentFormSettings).sort()) !==
			JSON.stringify(patchObject, Object.keys(patchObject).sort())
		)
	}

	async function previewSaveConfirmationModalConfirmed() {
		if (formElement.current) {
			if (formElement.current.checkValidity()) {
				shouldShowPreviewModal = true
				submitForm()
			} else {
				formElement.current.reportValidity()
			}
		}
	}

	function submitForm(e?: React.FormEvent<HTMLFormElement>) {
		if (e) {
			e.preventDefault()
		}

		return patch(accessToken, {
			applicationFormSettings: createPatchObject(),
		})
	}

	return (
		<div className={"card"}>
			<div className={"org-settings-content application-form-settings"}>
				<Admonition
					type={"is-danger"}
					icon={"alert-triangle--interface-essential"}
					className={"application-form-v2-admonition"}
					iconHasSemanticColor={true}
					isFullWidth={true}
					message={
						<p className={"application-form-v2-admonition-message"}>
							The current Application Form will be sunset by February 18th, 2025, and replaced by the new White-Label
							Application Form.{" "}
							<a
								href={"https://www.unit.co/docs/white-label-uis/white-label-application-form/"}
								target="_blank"
								rel="noreferrer"
							>
								Learn more
							</a>
						</p>
					}
				/>
				<h3>
					<span>Application Form</span>
					<CustomDropdown
						options={versionsDropdownOptions}
						selectedValue={selectedVersion}
						onSelect={(val) => setSelectedVersion(val)}
						style={{width: 320}}
					/>
				</h3>
				{showApplicationFormV2FeatureHighlightBanner && (
					<FeatureHighlightBanner
						imgUrl={FeatureHighlightImage}
						title={"Introducing White-Label Application form!"}
						showCloseButton={true}
						onClose={() => setShowApplicationFormV2FeatureHighlightBanner(false)}
						description={
							<>
								Our new and improved application form is now available. Check out the{" "}
								<a
									onClick={() => {
										setSelectedVersion(ApplicationFormVersion.V2)
										showPreviewModal()
									}}
								>
									preview
								</a>{" "}
								and{" "}
								<a
									href={"https://www.unit.co/docs/white-label-uis/white-label-application-form/"}
									target="_blank"
									rel="noreferrer"
								>
									documentation <Icon icon="maximize-square-4--interface-essential" size={12} />
								</a>
							</>
						}
					/>
				)}
				<form ref={formElement} onSubmit={(e) => submitForm(e)}>
					<fieldset>
						<div className="field">
							<label className="label">Name</label>
							<div className="control">
								<input
									className="input"
									required
									type="text"
									value={applicationFormBrandName ?? ""}
									placeholder={"Unit Finance"}
									onChange={(e) => setApplicationFormBrandName(e.target.value)}
								/>
							</div>
						</div>
						{selectedVersion == ApplicationFormVersion.V1 && (
							<div className="field">
								<label className="label">Logo (SVG)</label>
								<div className="field-body">
									<div className="file">
										<label className="file-label">
											<input
												className="file-input"
												type="file"
												onChange={(e) => onFileUploaded(e, setApplicationFormLogo)}
												accept=".svg"
												required={!applicationFormLogo}
											/>
											<span className="file-cta">
												<span className="file-icon">
													<i className="fas fa-upload" />
												</span>
												<span className="file-label">Choose a file…</span>
											</span>
										</label>
									</div>
								</div>
								{applicationFormLogo ? <img className="org-settings-logo" src={applicationFormLogo} /> : null}
							</div>
						)}
						{selectedVersion == ApplicationFormVersion.V1 && (
							<div className="field">
								<label className="label">Favicon (Square PNG)</label>
								<div className="field-body">
									<div className="file">
										<label className="file-label">
											<input
												className="file-input favicon-input"
												type="file"
												onChange={(e) => onFileUploaded(e, setApplicationFormFavicon)}
												accept=".png"
												required={!applicationFormFavicon}
											/>
											<span className="file-cta">
												<span className="file-icon">
													<i className="fas fa-upload" />
												</span>
												<span className="file-label">Choose a file…</span>
											</span>
										</label>
									</div>
								</div>
								{applicationFormFavicon ? (
									<img
										className="org-settings-logo"
										src={applicationFormFavicon}
										onLoad={(e) => onFaviconImageLoadedSquareValidation(e as React.ChangeEvent<HTMLImageElement>)}
									/>
								) : null}
							</div>
						)}
						<div className="field">
							<label className="label">Privacy Policy URL</label>
							<div className="control">
								<input
									className="input"
									type="url"
									value={applicationFormPrivacyPolicyUrl ?? ""}
									onChange={(e) => setApplicationFormPrivacyPolicyUrl(e.target.value)}
								/>
							</div>
						</div>
						<div className="field">
							<label className="label">Consent to Electronic Disclosures URL</label>
							<div className="control">
								<input
									className="input"
									type="url"
									value={applicationFormElectronicDisclosuresUrl ?? ""}
									onChange={(e) => setApplicationFormElectronicDisclosuresUrl(e.target.value)}
								/>
							</div>
						</div>
						{selectedVersion == ApplicationFormVersion.V2 && (
							<div className="field">
								<label className="label" id="showElectronicDisclosureStage">
									<input
										type="checkbox"
										checked={applicationFormShowElectronicDisclosureStage}
										onChange={(e) => setApplicationFormShowElectronicDisclosureStage(e.target.checked)}
									/>{" "}
									Show Electronic Disclosure Stage
								</label>
							</div>
						)}
						{selectedVersion == ApplicationFormVersion.V2 && (
							<div className="field">
								<label className="label" id="showTaxStage">
									<input
										type="checkbox"
										checked={applicationFormShowTaxStage}
										onChange={(e) => setApplicationFormShowTaxStage(e.target.checked)}
									/>{" "}
									Show Tax Stage
								</label>
							</div>
						)}
						<div className="field">
							<label className="label">Deposit Terms & Conditions URL</label>
							<div className="control">
								<input
									className="input"
									type="url"
									value={applicationFormDepositTermsUrl ?? ""}
									onChange={(e) => setApplicationFormDepositTermsUrl(e.target.value)}
								/>
							</div>
						</div>
						<div className="field">
							<label className="label">Client Terms of Service URL</label>
							<div className="control">
								<input
									className="input"
									type="url"
									value={applicationFormClientTermsUrl ?? ""}
									onChange={(e) => setApplicationFormClientTermsUrl(e.target.value)}
								/>
							</div>
						</div>
						<div className="field">
							<label className="label">Cardholder Terms and Conditions URL</label>
							<div className="control">
								<input
									className="input"
									type="url"
									value={applicationFormCardholderTermsUrl ?? ""}
									onChange={(e) => setApplicationFormCardholderTermsUrl(e.target.value)}
								/>
							</div>
						</div>
						<div className="field">
							<label className="label">Cash Advance Terms and Conditions URL</label>
							<div className="control">
								<input
									className="input"
									type="url"
									value={applicationFormCashAdvancedTermsUrl ?? ""}
									onChange={(e) => setApplicationFormCashAdvancedTermsUrl(e.target.value)}
								/>
							</div>
						</div>
						<div className="field">
							<label className="label">Debit Card Disclosure URL</label>
							<div className="control">
								<input
									className="input"
									type="url"
									value={applicationFormDebitCardDisclosureUrl ?? ""}
									onChange={(e) => setApplicationFormDebitCardDisclosureUrl(e.target.value)}
								/>
							</div>
						</div>
						<div className="field">
							<label className="label">Additional Disclosures</label>
							<div className="control">
								<KeyValueEditor
									keyValues={applicationFormAdditionalDisclosures ?? []}
									setKeyValues={setApplicationFormAdditionalDisclosures}
									keyName={"title"}
									valueName={"url"}
									itemName={"Disclosure"}
									keyPlaceholder={"Disclosure name"}
									valuePlaceholder={"Disclosure URL"}
									valueInputType={"url"}
								/>
							</div>
						</div>
						<div className="field">
							<label className="label">Contact Us Email</label>
							<div className="control">
								<input
									className="input"
									type="email"
									value={applicationFormContactUsEmail ?? ""}
									onChange={(e) => setApplicationFormContactUsEmail(e.target.value)}
								/>
							</div>
						</div>
						<div className="field">
							<label className="label">Redirect URL</label>
							<div className="control">
								<input
									className="input"
									type="url"
									value={applicationFormRedirectUrl ?? ""}
									onChange={(e) => setApplicationFormRedirectUrl(e.target.value)}
								/>
							</div>
						</div>
						<div className="field">
							<label className="label redirect-button-text-description">
								Redirect Button Text (Deprecated. Use{" "}
								<a href="https://www.unit.co/docs/white-label-uis/language/" target="_blank" rel="noreferrer">
									White-Label Language <Icon icon="maximize-square-4--interface-essential" size={12} />
								</a>
								)
							</label>
							<div className="control">
								<input
									className="input"
									type="text"
									value={applicationFormRedirectButtonText ?? ""}
									onChange={(e) => setApplicationFormRedirectButtonText(e.target.value)}
								/>
							</div>
						</div>
						<div className="field">
							<label className="label">Denied Redirect URL</label>
							<div className="control">
								<input
									className="input"
									type="url"
									value={applicationFormDeniedRedirectUrl ?? ""}
									onChange={(e) => setApplicationFormDeniedRedirectUrl(e.target.value)}
								/>
							</div>
						</div>
						{selectedVersion == ApplicationFormVersion.V1 && (
							<div className="field">
								<label className="label">Custom CSS</label>
								<div className="control">
									<Editor
										language="css"
										height={300}
										theme="vs-dark"
										value={applicationFormCustomCss ?? ""}
										onChange={setApplicationFormCustomCss}
									/>
								</div>
							</div>
						)}
						{selectedVersion == ApplicationFormVersion.V1 && (
							<div className="field">
								<label className="label" id="validatePhoneNumber">
									<input
										type="checkbox"
										checked={applicationFormValidatePhoneNumber}
										onChange={(e) => setApplicationFormValidatePhoneNumber(e.target.checked)}
									/>{" "}
									Validate Phone Number
									<label className="tooltip-container">
										<a data-tip="React-tooltip" data-event="click focus">
											<FontAwesomeIcon icon={faInfoCircle} color="#545558" />
											<ReactTooltip
												place="right"
												effect="solid"
												className="tooltip-info"
												globalEventOff="click"
												border={true}
												borderColor="#C4C4C4"
												backgroundColor="#F5F5F5"
												textColor="black"
												data-html={true}
											>
												You should only disable this if you are validating and pre-populating the phone number. When the
												phone number validation is disabled, the customer will not be able to edit the phone number from
												within the application form
											</ReactTooltip>
										</a>
									</label>
								</label>
							</div>
						)}
						{selectedVersion == ApplicationFormVersion.V2 && (
							<div className="field">
								<label className="label" id="useSelfieVerification">
									<input
										type="checkbox"
										checked={applicationFormUseSelfieVerification}
										onChange={(e) => setApplicationFormUseSelfieVerification(e.target.checked)}
									/>{" "}
									Use Selfie Verification
								</label>
							</div>
						)}
						{selectedVersion == ApplicationFormVersion.V2 && (
							//Remove display none to enable outreach
							<div className="field">
								<label className="label" id="enableOutreach">
									<input
										type="checkbox"
										checked={applicationFormEnableOutreach}
										onChange={(e) => setApplicationFormEnableOutreach(e.target.checked)}
									/>{" "}
									Enable outreach
								</label>
							</div>
						)}
						{selectedVersion == ApplicationFormVersion.V2 && applicationFormEnableOutreach && outreachSettings.isOk() && (
							<>
								<div className="field">
									<label className="label">Outreach Settings Id</label>
									<div className="select">
										<select
											className="input"
											value={applicationFormOutreachSettingsId ?? ""}
											onChange={(e) => setApplicationFormOutreachSettingsId(e.target.value)}
										>
											{isUndefined(applicationFormOutreachSettingsId) ? <option key="default" defaultValue="" /> : null}
											{outreachSettings.value.map((settings) => (
												<option key={settings.id} value={settings.id}>
													{settings.id}
												</option>
											))}
										</select>
									</div>
								</div>
								<div className="field">
									<label className="label">Banking Page URL</label>
									<div className="control">
										<input
											className="input"
											type="string"
											value={bankingPageURL ?? ""}
											onChange={(e) => setBankingPageURL(e.target.value)}
										/>
									</div>
								</div>
							</>
						)}
					</fieldset>
					<div className="columns">
						<div className="column mt-4">
							<button
								type="button"
								className="button is-info mr-2"
								onClick={(e) => {
									e.preventDefault()
									if (hasFormChanged()) {
										showPreviewSaveConfirmationModal()
									} else {
										showPreviewModal()
									}
								}}
							>
								Preview
							</button>
							{state.match(
								() => (
									<button type="submit" className="button is-success">
										Update
									</button>
								),
								() => (
									<button type="submit" className="button is-success is-loading">
										Updating
									</button>
								),
								(_) => null,
								(err) => (
									<>
										<div className="has-text-danger">
											{err.errors ? err.errors[0].title : "An unexpected error has occurred"}
										</div>
										<button type="submit" className="button is-success">
											Update
										</button>
									</>
								)
							)}
						</div>
					</div>
				</form>
			</div>
		</div>
	)
}

export function PreviewSaveConfirmationModal({close, onSubmit}: {close: () => void; onSubmit: () => void}) {
	return (
		<ConfirmationModal
			close={close}
			onSubmit={onSubmit}
			title={"Save Changes"}
			okButtonClassname={"button is-success is-outlined"}
			cancelButtonClassname="button is-white"
			okButtonText={"Save"}
		>
			<p> Please save all changes before preview </p>
		</ConfirmationModal>
	)
}
